// Lightweight client diagnostics — no third-party SDK.
//
// Keeps a rolling buffer of the last N events (GraphQL errors, empty-list /
// data-loss signals, response summaries) in memory + localStorage so support
// can pull them from a complaining user's browser via window.__diag().
//
// If VUE_APP_CLIENT_LOG_URL is set, each event is also POSTed (fire-and-forget,
// keepalive) to that endpoint with the user's bearer token. When it is unset
// the module is purely local — so it can ship and be useful before the
// collection endpoint exists.

const MAX_EVENTS = 50
const LS_KEY = 'do_diag_buffer'
const REMOTE_URL = process.env.VUE_APP_CLIENT_LOG_URL

let buffer = []
try {
  buffer = JSON.parse(window.localStorage.getItem(LS_KEY)) || []
} catch (e) {
  buffer = []
}

function persist () {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(buffer))
  } catch (e) {
    // localStorage full / unavailable — keep the in-memory buffer only.
  }
}

// Snapshot of the state that explains most silent failures (why a list is
// empty for one user but not another). Read lazily off the global app instance
// to avoid an import cycle with the store.
function snapshot () {
  const out = {
    time: new Date().toISOString(),
    graphqlEndpoint: process.env.VUE_APP_GRAPHQL_HTTP,
    env: process.env.VUE_APP_ENV
  }
  try {
    out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {}
  try {
    const store = window.doApp && window.doApp.$store
    if (store) {
      const s = store.state
      out.selectedDate = s.selectedDate
      out.racesInState = s.races ? Object.keys(s.races).length : null
      out.racesAfterFilters = (store.getters.getRaces || []).length
      out.navFilters = s.userConfig && s.userConfig.raceNavFilters
      out.username = s.account && s.account.user && s.account.user.username
    }
  } catch (e) {}
  return out
}

export function logEvent (level, type, data) {
  let event
  try {
    event = { t: new Date().toISOString(), level, type, data }
    buffer.push(event)
    if (buffer.length > MAX_EVENTS) buffer = buffer.slice(-MAX_EVENTS)
    persist()
    // eslint-disable-next-line no-console
    const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log
    fn(`[diag:${type}]`, data)
    // Surface the latest error detail to the "something went wrong" banner so a
    // support screenshot is actionable.
    if (level === 'error') {
      try {
        const store = window.doApp && window.doApp.$store
        if (store) {
          const message = (data && (data.message || (data.errors && data.errors.join('; ')))) || ''
          store.commit('setLastError', { type, message, time: event.t })
        }
      } catch (e) {}
    }
    sendRemote(event)
  } catch (e) {
    // Diagnostics must never break the app.
  }
  return event
}

function sendRemote (event) {
  if (!REMOTE_URL) return
  try {
    const token = window.localStorage.getItem('coreapi_auth_token')
    if (!token) return
    // keepalive so the request survives a page unload; auth header means we
    // can't use sendBeacon (it can't set headers), so plain fetch it is.
    fetch(REMOTE_URL, {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ event, ctx: snapshot() })
    }).catch(() => {})
  } catch (e) {
    // never throw from logging
  }
}

export function getDiagnostics () {
  return { snapshot: snapshot(), events: buffer }
}

export function clearDiagnostics () {
  buffer = []
  persist()
}

if (typeof window !== 'undefined') {
  // Support/debug hook: `__diag()` in the console dumps the buffer + snapshot.
  window.__diag = () => {
    const d = getDiagnostics()
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(d, null, 2))
    return d
  }
}
